<template>
    <v-card class="elevation-1">
        <v-toolbar flat height="37">
            <span class="primary--text font-weight-bold">RECENT TIME LOGS</span>
            <v-spacer />
        </v-toolbar>
        
        <!-- MOBILE -->
        <v-expansion-panel v-if="isMobile" v-model="expandedPanels" expand>
            <v-expansion-panel-content
                v-for="(item, i) in my_attendance_data.slice(0, 7)"
                :key="i"
            >
            <template v-slot:header>
                <b>
                {{ item.transDate | moment('MMM DD ddd') }}
                {{ item.isToday ? ' | Today' : '' }}
                </b>
                <v-spacer />
            </template>

            <v-card class="elevation-1">
                <v-card-text class="subheading">

                <!-- WORKSHIFT -->
                <v-layout row wrap @click="viewAttendance(item)">
                    <v-flex xs4>
                    <v-chip label color="primary" outline class="wp-100">
                        WORKSHIFT
                    </v-chip>
                    </v-flex>

                    <v-flex xs8>
                    <v-chip
                        v-if="item.clockStart"
                        label
                        :color="item.workshiftColor"
                        class="wp-100"
                    >
                        {{ item.clockStart | moment('h:mm a') }}
                        -
                        {{ item.clockEnd | moment('h:mm a') }}
                    </v-chip>

                    <v-chip v-else label color="primary" outline class="wp-100">
                        <span class="red--text">No workshift</span>
                    </v-chip>
                    </v-flex>
                </v-layout>

                <!-- CLOCK IN -->
                <v-layout row wrap @click="viewAttendance(item)">
                    <v-flex xs4>
                        <v-chip label color="primary" outline class="wp-100">
                            CLOCK IN
                        </v-chip>
                    </v-flex>

                    <v-flex xs8>
                        <v-chip
                            v-if="!item.isToday || item.clockIn"
                            label
                            color="primary"
                            outline
                            class="wp-100"
                        >
                            <b>{{ item.clockIn | moment('hh:mm a') }}</b>
                        </v-chip>

                        <v-btn
                            v-if="item.isToday && item.needsClockIn && !item.clockIn"
                            block
                            class="primary clock-button"
                            :disabled="!item.clockStart"
                        >
                            Clock-In
                        </v-btn>
                    </v-flex>
                </v-layout>

                <!-- CLOCK OUT -->
                <v-layout row wrap @click="viewAttendance(item)">
                    <v-flex xs4>
                        <v-chip label color="primary" outline class="wp-100">
                            CLOCK OUT
                        </v-chip>
                    </v-flex>

                    <v-flex xs8>
                        <v-chip
                            v-if="!item.isToday || item.clockOut || item.needsClockOut"
                            label
                            color="primary"
                            outline
                            class="wp-100"
                        >
                            <b>{{ item.clockOut | moment('hh:mm a') }}</b>
                        </v-chip>

                        <v-btn
                            v-if="item.isToday && !isIncomplete(item) && item.needsClockOut"
                            block
                            class="primary clock-button"
                        >
                            Clock-Out
                        </v-btn>
                    </v-flex>
                </v-layout>

                <!-- CHARGEABLE -->
                <v-layout row wrap @click="viewAttendance(item)">
                    <v-flex xs4>
                        <v-chip label color="primary" outline class="wp-100">
                            CHARGEABLE
                        </v-chip>
                    </v-flex>

                    <v-flex xs8>
                        <v-chip label color="primary" outline class="wp-100">
                            <b v-if="item.isToday && item.needsClockOut"
                                :class="getLiveHoursClass(item)">
                            {{ getLiveHours(item) }}
                            </b>
                            <b v-else>
                            {{ item.chargeableHours }}
                            </b>
                        </v-chip>
                    </v-flex>
                </v-layout>

                <!-- STATUS -->
                <v-layout row wrap @click="viewAttendance(item)">
                    <v-flex xs4>
                        <v-chip label color="primary" outline class="wp-100">
                            STATUS
                        </v-chip>
                    </v-flex>

                    <v-flex xs8>
                        <v-chip
                            v-if="item.isToday && isIncomplete(item)"
                            label
                            color="primary"
                            outline
                            class="wp-100"
                        >
                            <b class="red--text">Incomplete Hours</b>
                        </v-chip>

                        <v-btn
                            v-else-if="canClockOutFromPreviousDay(item)"
                            block
                            class="primary clock-button"
                        >
                            Clock-Out
                        </v-btn>

                        <v-chip
                            v-else
                            label
                            color="primary"
                            outline
                            class="wp-100"
                        >
                            <b
                            v-if="!item.needsClockIn && !item.needsClockOut"
                            :class="item.remarksColor"
                            >
                            {{ item.remarks }}
                            </b>
                        </v-chip>
                    </v-flex>
                </v-layout>

                </v-card-text>
            </v-card>
            </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- DESKTOP -->
        <v-data-table
            v-else
            :headers="header_myattendance"
            :items="my_attendance_data"
            :loading="loading_myattendance"
            :rows-per-page-items="rowsPerPageItems"
            :pagination.sync="paginationSync"
        >
            <template v-slot:items="props">
                <tr @click="viewAttendance(props.item)" class="clickable">

                    <!-- DATE -->
                    <td>
                        <span v-if="props.item.isToday">Today</span>
                        <span v-else>
                            {{ props.item.transDate | moment('MMM DD ddd') }}
                        </span>
                    </td>

                    <!-- WORKSHIFT -->
                    <td>
                        <v-chip
                            v-if="props.item.clockStart"
                            small
                            :style="`background-color:${props.item.workshiftColor}`"
                            class="ml-0"
                        >
                            {{ props.item.clockStart | moment('h:mma') }}
                            -
                            {{ props.item.clockEnd | moment('h:mma') }}
                        </v-chip>
                        <span v-else class="red--text">No workshift</span>
                    </td>

                    <!-- CLOCK IN -->
                    <td>{{ props.item.clockIn | moment('hh:mm a') }}</td>

                    <!-- CLOCK OUT -->
                    <td>{{ props.item.clockOut | moment('hh:mm a') }}</td>

                    <!-- CHARGEABLE -->
                    <td>
                        <span v-if="props.item.isToday && props.item.needsClockOut"
                                :class="getLiveHoursClass(props.item)">
                            {{ getLiveHours(props.item) }}
                        </span>
                        <span v-else>
                            {{ props.item.chargeableHours }}
                        </span>
                    </td>

                    <!-- STATUS -->
                    <td>
                        <span v-if="props.item.isToday">

                            <v-btn
                                v-if="props.item.needsClockIn"
                                small
                                class="primary clock-button"
                                :disabled="!props.item.clockStart"
                            >
                                Clock-In
                            </v-btn>

                            <span
                                v-if="isIncomplete(props.item) && !props.item.needsClockIn && props.item.needsClockOut"
                                class="red--text"
                            >
                                Incomplete Hours
                            </span>

                            <v-btn
                                v-if="!isIncomplete(props.item) && !props.item.needsClockIn && props.item.needsClockOut"
                                small
                                class="primary clock-button"
                            >
                                Clock-Out
                            </v-btn>
                        </span>

                        <span v-else-if="canClockOutFromPreviousDay(props.item)">
                            <v-btn small class="primary clock-button">
                                Clock-Out
                            </v-btn>
                        </span>

                        <span v-else>
                            <span
                                v-if="!props.item.needsClockIn && !props.item.needsClockOut"
                                :class="props.item.remarksColor"
                                >
                                {{ props.item.remarks }}
                            </span>
                        </span>
                    </td>

                </tr>
            </template>
        </v-data-table>
        
    </v-card>
</template>

<script>
import myAttendanceApi from '@/api/myattendance'
import rowsPerPageItems from '@/constants/rows-per-page'
import { getRowsPerPage, updateRowsPerPage } from '@/helpers/user-prefs'

export default {
    name: 'RecentTimeLogs',

    data() {
        return {
            rowsPerPageItems,
            expandedPanels: [true],

            loading_myattendance: false,
            my_attendance_data: [],

            paginationSync: {
                rowsPerPage: getRowsPerPage(this.$route.name)
            },

            header_myattendance: [
                { text: 'Date', sortable: false },
                { text: 'Workshift', sortable: false },
                { text: 'Clock-In', sortable: false },
                { text: 'Clock-Out', sortable: false },
                { text: 'Chargeable', sortable: false },
                { text: 'Status', sortable: false },
            ],

            error: false,
            errorMessage: '',

            timer: null,
            tick: 0
        }
    },
    methods: {
        async getMyAttendance() {
            try {
                this.loading_myattendance = true
                const res = await myAttendanceApi.getPastWeek()

                if (res && res.status === 200) {
                    this.my_attendance_data = res.data.items || []
                }
            } catch (err) {
                this.error = true
                this.errorMessage = err
            } finally {
                this.loading_myattendance = false
            }
        },
        viewAttendance(item) {
            const today = this.$moment().isSame(item.transDate, 'day')

            if (this.canClockOutFromPreviousDay(item) || (today && (item.needsClockIn || item.needsClockOut))) {
                this.$router.push('/myattendance/daily')
                return
            }

            this.$router.push(
                `/myattendance/daily/${this.$moment(item.transDate).format('YYYY-MM-DD')}`
            )
        },
        canClockOut(item) {
            if (!item || !item.clockIn || item.clockOut) {
                return false
            }

            if (!item.clockStart) {
                return false
            }

            const now = this.$moment()
            const shiftStart = this.$moment(item.clockStart)

            // Once 24 hours have passed from the scheduled shift start,
            // do not allow clock-out anymore.
            return now.diff(shiftStart, 'hours', true) < 24
        },
        canClockOutFromPreviousDay(item) {
            if (!item || !item.clockIn || item.clockOut) {
                return false
            }

            const clockIn = this.$moment(item.clockIn)

            const yesterday = this.$moment()
                .subtract(1, 'day')
                .format('YYYY-MM-DD')

            return clockIn.format('YYYY-MM-DD') === yesterday
        },
        isIncomplete(item) {
            if (!item) return false

            if (!item.clockIn) {
                return true
            }

            const totalHours = this.$moment().diff(
                this.$moment(item.clockIn),
                'hours',
                true
            )

            return totalHours < 9
        },
        getLiveHours(item) {
            this.tick // THIS creates a reactive dependency

            if (!item || !item.clockIn) return '0:00:00'

            const d = this.$moment.duration(
                this.$moment().diff(this.$moment(item.clockIn))
            )

            return `${d.hours()}:${d.minutes()}:${d.seconds()}`
        },
        getLiveHoursClass(item) {
            if (!item || !item.clockIn) return ''

                const h = this.$moment.duration(
                this.$moment().diff(this.$moment(item.clockIn))
                ).hours()

                return h >= 9 ? 'green--text' : 'red--text'
        }
    },
    async created() {
        await this.getMyAttendance()

        this.timer = setInterval(() => {
            this.tick++
        }, 1000)
    },
    watch: {
        'paginationSync.rowsPerPage'(val) {
            updateRowsPerPage(this.$route.name, val)
        }
    },
    destroyed() {
        clearInterval(this.timer)
    },
}
</script>

<style scoped>
.clock-button {
    margin: 2px -3px 0px 3px;
}
</style>